<script lang="ts" setup>
import { computed, ComputedRef } from 'vue';
import {
    CallModel,
    CallTranscriptModel,
    CallParticipantType,
    CallBulletType,
    CallPlayerGetters,
} from '@/types';
import { PlayIcon } from '@heroicons/vue/solid';

import store from '@/store';

const { call, callDuration, bulletType, participantType } = defineProps({
    call: {
        type: Object as () => CallModel,
        required: true,
    },
    callDuration: {
        type: Number,
        required: true,
    },
    bulletType: {
        type: String as () => CallBulletType,
        required: true,
    },
    participantType: {
        type: String as () => CallParticipantType,
        required: true,
    },
});

const isBulletTypeRegistered = bulletType === CallBulletType.registered;
const isBulletTypeMatched = bulletType === CallBulletType.matched;

const emit = defineEmits(['playSnippet']);

const callPlayerSearchQuery: ComputedRef<string> = computed(
    () => store.getters[CallPlayerGetters.SEARCH_QUERY],
);
const callPlayerSelectedWords: ComputedRef<string[]> = computed(() => {
    const selectedWordsMap = store.getters[CallPlayerGetters.SELECTED_WORDS];
    return Array.from(selectedWordsMap.get(participantType) ?? []);
});

const bullets = computed(() => {
    if (!call?.transcripts) return [];

    return call.transcripts
        .filter(({ participant }) => participant === participantType)
        .map((transcript) => {
            const matchedWords = transcript.words.filter((word) => {
                const query = callPlayerSearchQuery.value;
                const selectedWords = callPlayerSelectedWords.value;
                return word === query || selectedWords.includes(word);
            });

            return {
                ...transcript,
                matchedWords,
            };
        })
        .filter(({ registeredWords = [], matchedWords = [] }) => {
            return (
                (isBulletTypeRegistered && registeredWords.length > 0) ||
                (isBulletTypeMatched && matchedWords.length > 0)
            );
        });
});

function playSnippet(start: number, end: number) {
    emit('playSnippet', start, end);
}

function getBulletPosition(item: CallTranscriptModel) {
    return (item.startedAt / callDuration) * 100 + '%';
}

function getWordsByBulletType(transcript: CallTranscriptModel): string[] {
    let words: string[] = [];

    if (isBulletTypeRegistered) words = transcript.registeredWords;
    else if (isBulletTypeMatched) words = transcript.matchedWords;

    const uniqueSortedWords = [...new Set(words)].sort();

    return uniqueSortedWords;
}

const variant = bulletType == CallBulletType.registered ? 'blue' : 'orange';
const playColor = `text-${variant}-500`;
const bulletColor = `bg-${variant}-500/50`;
</script>

<template>
    <div v-if="callDuration" class="w-100 relative h-4">
        <div
            v-for="bullet in bullets"
            :key="`bullet-${bulletType}-${participantType}-${bullet.id}`"
            class="group absolute h-4 w-4 rounded-full p-0 hover:-m-2 hover:h-8 hover:w-8 hover:bg-gray-50 hover:transition-all"
            :class="bulletColor"
            :style="{ left: getBulletPosition(bullet) }"
        >
            <PlayIcon
                class="invisible h-full w-full cursor-pointer group-hover:visible"
                :class="playColor"
                @click="playSnippet(bullet.startedAt, bullet.endedAt)"
            />
            <div
                class="invisible absolute -ml-1 flex flex-col rounded border border-gray-200 bg-gray-50 px-2 group-hover:visible"
            >
                <div
                    v-for="word in getWordsByBulletType(bullet)"
                    :key="`bullet-${bulletType}-${participantType}-${bullet.id}-${word}`"
                    class="z-50 whitespace-nowrap text-gray-700"
                >
                    {{ word }}
                </div>
            </div>
        </div>
    </div>
</template>
